import { useState } from "react";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { api, formatApiErrorDetail } from "../lib/api";
import { SERVICE_OPTIONS, PHONE_DISPLAY, PHONE_TEL, EMAIL } from "../lib/content";

const FIELDS = [
  { name: "name", label: "Name", type: "text", required: true, half: true },
  { name: "business_name", label: "Business Name", type: "text", half: true },
  { name: "phone", label: "Phone", type: "tel", required: true, half: true },
  { name: "email", label: "Email", type: "email", required: true, half: true },
  { name: "pickup_location", label: "Pickup Location", type: "text", half: true },
  { name: "delivery_location", label: "Delivery Location", type: "text", half: true },
];

const empty = {
  name: "", business_name: "", phone: "", email: "",
  pickup_location: "", delivery_location: "", service_needed: "", message: "",
};

export const ContactForm = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Please fill in your name, phone, and email.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/quotes", form);
      setDone(true);
      setForm(empty);
      toast.success("Quote request sent! We'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact" className="bg-[#F8FAFC] py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
          <Reveal>
            <Eyebrow testid="contact-eyebrow">Get Started</Eyebrow>
            <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-navy lg:text-5xl">
              Request a quote in minutes
            </h2>
            <p className="mt-5 text-base leading-relaxed text-navy/70">
              Tell us about your delivery needs and we'll get right back to you with clear, competitive pricing.
              Same-day and recurring routes available across Metro Atlanta.
            </p>
            <div className="mt-8 space-y-4">
              <a href={`tel:${PHONE_TEL}`} className="block" data-testid="contact-phone">
                <span className="text-xs font-bold uppercase tracking-[0.18em] text-gold">Call</span>
                <p className="font-display text-2xl font-bold text-navy">{PHONE_DISPLAY}</p>
              </a>
              <a href={`mailto:${EMAIL}`} className="block" data-testid="contact-email">
                <span className="text-xs font-bold uppercase tracking-[0.18em] text-gold">Email</span>
                <p className="font-display text-lg font-semibold text-navy">{EMAIL}</p>
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <form
              onSubmit={submit}
              data-testid="quote-form"
              className="rounded-[2rem] border border-navy/10 bg-white p-6 shadow-[0_20px_60px_rgba(11,46,89,0.08)] sm:p-9"
            >
              {done ? (
                <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="quote-success">
                  <CheckCircle2 className="h-16 w-16 text-gold" />
                  <h3 className="mt-5 font-display text-2xl font-bold text-navy">Request received</h3>
                  <p className="mt-2 max-w-sm text-navy/60">Thanks! A member of our team will reach out shortly to confirm details and pricing.</p>
                  <button type="button" onClick={() => setDone(false)} data-testid="quote-another-btn" className="mt-6 rounded-full border border-navy/20 px-6 py-2.5 text-sm font-semibold text-navy transition-colors hover:bg-navy hover:text-white">
                    Submit another request
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {FIELDS.map((f) => (
                      <div key={f.name} className={f.half ? "" : "sm:col-span-2"}>
                        <label className="mb-1.5 block text-sm font-medium text-navy">
                          {f.label}{f.required && <span className="text-gold"> *</span>}
                        </label>
                        <input
                          type={f.type}
                          value={form[f.name]}
                          onChange={set(f.name)}
                          required={f.required}
                          data-testid={`field-${f.name}`}
                          className="w-full rounded-xl border border-navy/15 bg-white px-4 py-3 text-navy transition-colors placeholder:text-navy/30 focus:border-gold"
                          placeholder={f.label}
                        />
                      </div>
                    ))}
                    <div className="sm:col-span-2">
                      <label className="mb-1.5 block text-sm font-medium text-navy">Service Needed</label>
                      <select
                        value={form.service_needed}
                        onChange={set("service_needed")}
                        data-testid="field-service_needed"
                        className="w-full rounded-xl border border-navy/15 bg-white px-4 py-3 text-navy focus:border-gold"
                      >
                        <option value="">Select a service</option>
                        {SERVICE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="mb-1.5 block text-sm font-medium text-navy">Message</label>
                      <textarea
                        rows={4}
                        value={form.message}
                        onChange={set("message")}
                        data-testid="field-message"
                        className="w-full resize-none rounded-xl border border-navy/15 bg-white px-4 py-3 text-navy transition-colors placeholder:text-navy/30 focus:border-gold"
                        placeholder="Tell us about your delivery needs..."
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="quote-submit-btn"
                    className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-gold py-4 text-base font-bold text-navy shadow-[0_12px_36px_rgba(201,162,39,0.35)] transition-[transform,opacity] duration-200 hover:scale-[1.01] disabled:opacity-60"
                  >
                    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                    {loading ? "Sending..." : "Request Quote"}
                  </button>
                </>
              )}
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
